<template>
  <StickyListHeader
    document-class="federal-flsa-dashboard"
    :header-columns="headerColumns"
    :selected-sort="selectedSort"
    :sort-direction="sortDirection"
    top-offset="311px"
    @submit-sort="submitSort"
  />
  <ListFilterEmptyState
    v-if="showEmptyState"
    message="No draft profiles match your filters or search. Try adjusting your filters or search term."
  />
  <LibraryList v-else>
    <LibraryListItemWrapper
      v-for="profileItem in profiles"
      :key="profileItem.profile.ulid"
      :class="{ 'flsa-survey-list-item-selected': isProfileSelected(profileItem.profile.ulid) }"
    >
      <router-link
        class="flsa-survey-list-link"
        :to="{
          query: {
            ...route.query,
            preview: profileItem.profile.ulid,
            type: 'flsaReport',
          },
        }"
        @click="handlePreviewClick"
      >
        <FlsaSurveyListItem
          class="flsa-survey-list-item"
          :item="profileItem"
        />
      </router-link>
    </LibraryListItemWrapper>
  </LibraryList>

  <StickyPagination
    v-if="totalPages > 1"
    :model-value="page"
    :length="totalPages"
    @update:model-value="(value) => sendFilterRequest({
      pageNumber: value,
      router,
    })"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import { useFlsaSurveysStore } from '@/stores/jobProfiles/flsaSurveys';
import FlsaSurveyListItem from '@/components/library/flsaSurveys/FlsaSurveyListItem.vue';
import ListFilterEmptyState from '@/components/shared/ListFilterEmptyState.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import { useFilters } from '@/stores/filters/filters';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { useAppBase } from '@/stores/app/base';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import { stickyHeadersFlsaSurveys, getStickyHeaderSortColumnLabel } from '@/helpers/constants/stickyHeaders';
import { getTooltip } from '@/helpers/tooltips';
import { useTracking } from '@/stores/tracking/tracking';

const route = useRoute();
const router = useRouter();

const isProfileSelected = (ulid) => (
  route.query?.type === 'flsaReport' && route.query?.preview === ulid
);

const { totalPages, page } = storeToRefs(useAppPagination());
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { areFiltersApplied } = storeToRefs(useFilters());
const { profiles } = storeToRefs(useFlsaSurveysStore());

const { sendFilterRequest, submitSubMenu } = useFilters();
const { open: filterDrawerOpen } = storeToRefs(useFilterDrawer());
const { windowWidth } = storeToRefs(useAppBase());

const FILTER_DRAWER_OPEN_HIDE_HEADER_TOOLTIP_MAX_WIDTH = 1600;

const hasFiltersOrSearch = computed(() => {
  const hasSearch = Boolean(route.query?.q);

  return hasSearch || areFiltersApplied.value;
});

const showEmptyState = computed(() => (
  profiles.value.length === 0
  && hasFiltersOrSearch.value
));

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'title';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'title').startsWith('-')
    ? 'desc'
    : 'asc'
));

const headerColumns = computed(() => {
  const baseColumns = [
    stickyHeadersFlsaSurveys.jobProfile,
    stickyHeadersFlsaSurveys.jobLevel,
    stickyHeadersFlsaSurveys.alerts,
    stickyHeadersFlsaSurveys.formSubmittedBy,
    stickyHeadersFlsaSurveys.flsaStatus,
    stickyHeadersFlsaSurveys.exemptionBasis,
  ];

  const hideHeaderTooltips = filterDrawerOpen.value
    && windowWidth.value < FILTER_DRAWER_OPEN_HIDE_HEADER_TOOLTIP_MAX_WIDTH;

  return baseColumns.map((column) => {
    if (hideHeaderTooltips) {
      return { ...column, tooltip: null };
    }

    if (column.tooltip?.key) {
      const tooltip = getTooltip(column.tooltip.key);

      return {
        ...column,
        tooltip: tooltip.html ? tooltip : null,
      };
    }

    return column;
  });
});

const handlePreviewClick = () => {
  useTracking().flsaLibOpenedPreview();
};

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || 'title';
  const currentBase = current.startsWith('-') ? current.slice(1) : current;

  if (selectedId === '__select') return;

  let optionId;
  if (selectedId === currentBase) {
    optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;
  } else {
    optionId = selectedId;
  }

  const direction = optionId.startsWith('-') ? 'desc' : 'asc';

  useTracking().flsaLibSortedTable({
    column: getStickyHeaderSortColumnLabel(optionId, stickyHeadersFlsaSurveys),
    direction,
  });

  submitSubMenu({ optionId, router });
};
</script>

<style lang="less" scoped>
:deep(.sticky-header-items) {
  height: 100%;
}

:deep(.document-list-item-wrapper):not(:last-child) {
  border-bottom: 1px solid @stroke-default;
}

.flsa-survey-list-link {
  display: block;
  color: inherit;
  text-decoration: none;
}

:deep(.flsa-survey-list-item-selected) {
  background: @bg-canvas;
}

.flsa-survey-list-item,
:deep(.sticky-header-items.federal-flsa-dashboard) {
  grid-template-columns:
    minmax(140px, 1.6fr)
    minmax(80px, 0.7fr)
    minmax(165px, 0.9fr)
    minmax(100px, 1fr)
    minmax(80px, 0.8fr)
    minmax(100px, 1fr);
  height: 100%;
  gap: 0;
}

:deep(.sticky-header-items.federal-flsa-dashboard) {
  .job-profile,
  .job-level,
  .alerts,
  .form-submitted-by,
  .flsa-status,
  .exemption-basis {
    justify-self: start;
    padding-left: 16px;
  }
}

:deep(.sticky-header-items.federal-flsa-dashboard .sortable-column) {
  width: 100%;

  p {
    font-size: 12px !important;
    font-weight: 500 !important;
    line-height: 150% !important;
  }

  &.job-profile,
  &.job-level,
  &.alerts,
  &.form-submitted-by,
  &.flsa-status,
  &.exemption-basis {
    justify-content: flex-start;
  }
}
</style>
